import { useEffect, useMemo, useState } from 'react';
import { Calculator } from 'lucide-react';
import { financialApi, type FormulaPreview, type SalaryFormula } from '../api';

const DEFAULT_FORMULA = `base_salary + allowances_total + overtime_hours * 5000 + total_commission - total_advances - total_fines`;

const SAMPLE_VARS: Record<string, number> = {
  base_salary: 500000,
  allowances_total: 100000,
  overtime_hours: 12,
  total_commission: 25000,
  total_advances: 30000,
  total_fines: 5000,
  worked_hours: 200,
  regular_hours: 176,
  present_days: 22,
  leave_days: 0,
};

export function SalaryFormulaPage() {
  const [formulas, setFormulas] = useState<SalaryFormula[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [slug, setSlug] = useState('default');
  const [name, setName] = useState('Default Salary');
  const [text, setText] = useState(DEFAULT_FORMULA);
  const [vars, setVars] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(SAMPLE_VARS).map(([k, v]) => [k, String(v)])),
  );
  const [preview, setPreview] = useState<FormulaPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setFormulas(await financialApi.listFormulas());
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onSelect = (f: SalaryFormula) => {
    setSelected(f.id);
    setSlug(f.slug);
    setName(f.name);
    setText(f.formula_text);
    setPreview(null);
  };

  const runPreview = async () => {
    setError(null);
    setBusy(true);
    try {
      const numericVars: Record<string, number> = {};
      for (const [k, v] of Object.entries(vars)) {
        const n = Number(v);
        if (!Number.isFinite(n)) {
          setError(`Variable ${k!} is not numeric`);
          setBusy(false);
          return;
        }
        numericVars[k] = n;
      }
      setPreview(await financialApi.previewFormula(text, numericVars));
    } catch (e) {
      setError((e as Error).message || 'Preview failed');
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await financialApi.reviseFormula({ slug, name, formula_text: text });
      await load();
    } catch (e) {
      setError((e as Error).message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const variableList = useMemo(() => Object.keys(SAMPLE_VARS), []);

  return (
    <div className="flex h-full">
      <aside className="w-64 border-r border-border bg-surface p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Formulas
        </h2>
        <ul className="mt-3 space-y-1">
          {formulas.map((f) => (
            <li key={f.id}>
              <button
                className={`hover:bg-surface-accent w-full rounded px-3 py-2 text-left ${selected === f.id ? 'bg-surface-accent' : ''}`}
                onClick={() => onSelect(f)}
              >
                <div className="font-medium">{f.name}</div>
                <div className="text-xs text-muted-foreground">
                  {f.slug} · v{f.version}
                </div>
              </button>
            </li>
          ))}
        </ul>
        <button
          className="btn-secondary mt-4 w-full"
          onClick={() => {
            setSelected(null);
            setSlug('');
            setName('');
            setText('');
            setPreview(null);
          }}
        >
          + New formula
        </button>
      </aside>

      <main className="grid flex-1 grid-cols-2 overflow-hidden">
        <section className="overflow-y-auto border-r border-border p-6">
          <h1 className="page-title mb-4 flex items-center gap-2.5">
            <Calculator className="h-6 w-6 text-primary" />
            Salary Formula Builder
          </h1>

          {error && (
            <div className="mb-4 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Slug</label>
              <input
                className="input mt-1 w-full font-mono"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="default"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Name</label>
              <input
                className="input mt-1 w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">
                Formula
              </label>
              <textarea
                className="input mt-1 w-full font-mono text-sm"
                rows={6}
                value={text}
                onChange={(e) => setText(e.target.value)}
                spellCheck={false}
              />
              <div className="mt-1 text-xs text-muted-foreground">
                Available: min, max, round, floor, ceil, abs, clamp, if · variables below.
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button className="btn-primary" onClick={runPreview} disabled={busy}>
                Preview
              </button>
              <button
                className="btn-secondary"
                onClick={save}
                disabled={busy || !slug || !name || !text}
              >
                Save new version
              </button>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
              Variables
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {variableList.map((k) => (
                <div key={k}>
                  <label className="block font-mono text-xs text-muted-foreground">{k}</label>
                  <input
                    className="input mt-0.5 w-full font-mono text-sm"
                    value={vars[k] ?? ''}
                    onChange={(e) => setVars({ ...vars, [k]: e.target.value })}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="overflow-y-auto bg-background p-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Preview
          </h2>
          {!preview && (
            <div className="text-sm text-muted-foreground">
              Run the preview to see the value + trace.
            </div>
          )}
          {preview && (
            <>
              <div className="rounded border border-border bg-surface p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Result</div>
                <div className="mt-1 font-mono text-3xl font-semibold">{preview.value}</div>
              </div>

              <div className="mt-4 rounded border border-border bg-surface p-4">
                <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                  Variables referenced ({preview.variables_used.length})
                </div>
                <div className="flex flex-wrap gap-1">
                  {preview.variables_used.map((v) => (
                    <span
                      key={v}
                      className="bg-surface-accent rounded px-2 py-0.5 font-mono text-xs"
                    >
                      {v}
                    </span>
                  ))}
                </div>
              </div>

              {preview.trace.length > 0 && (
                <div className="mt-4 rounded border border-border bg-surface p-4">
                  <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                    Trace
                  </div>
                  <table className="w-full font-mono text-xs">
                    <tbody className="divide-y divide-border">
                      {preview.trace.map((s, i) => (
                        <tr key={i}>
                          <td className="py-1.5 text-muted-foreground">{s.label}</td>
                          <td className="py-1.5 text-right">{s.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
